import React, { useEffect, useState } from 'react';
import { asegurarCargada, buscarSuspendida, suscribir } from '../utils/suspendedCompanies';

// Shared company logo/icon renderer used EVERYWHERE a company logo appears
// (companies list/detail, tickets, invoicing, calendar, dashboard widgets,
// WhatsApp, portal, public pages…). One component = one consistent look.
//
// Why `object-fit: contain` (not `cover`): company logos come in every shape —
// square marks, wide wordmarks, tall icons. `cover` crops them to a square and
// makes wide/tall logos look chopped or "oval". `contain` shows the WHOLE logo,
// letterboxed inside a neutral rounded tile, so it always reads as the brand
// intended. A light tile + subtle border + small padding keeps dark, colored,
// and transparent logos legible on both light and dark themes.
//
// Props:
//   name       - company name (used for alt text + fallback initial)
//   logoUrl    - logo image URL (relative /uploads/... or absolute). Falsy → initial chip.
//   size       - px (square). Default 32.
//   labelColor - background color for the fallback initial chip.
//   radius     - override corner radius (defaults to a size-proportional value).
//   tile       - show the light backing tile behind the logo (default true).
//   style      - extra style merged onto the outer box.
//   title      - tooltip text.
//   companyId  - id de la empresa (opcional). Si viene, el aviso de "sin servicio" se
//                resuelve por id en vez de por nombre, que es más exacto.
//
// AVISO DE SERVICIO SUSPENDIDO: este componente se usa en las ~36 pantallas donde aparece
// una empresa, así que el aviso vive ACÁ y no en cada una. Una empresa suspendida se marca
// con un anillo rojo y un punto ⏸, y el tooltip dice "Cliente sin servicio de AyudaTecno".
// Así, cuando mañana alguien escriba una pantalla nueva que muestre empresas, el aviso ya
// viene puesto sin que nadie se acuerde de agregarlo.
export default function CompanyLogo({ name, logoUrl, size = 32, labelColor, radius, tile = true, style, title, className, companyId }) {
  const s = Number(size) || 32;
  const r = radius != null ? radius : Math.max(3, Math.round(s * 0.22));
  const pad = s <= 18 ? 1 : Math.round(s * 0.1);

  // Se re-renderiza cuando la lista compartida llega o cambia. Mientras no haya llegado,
  // se dibuja exactamente como antes: el badge nunca puede romper el logo.
  const [, redibujar] = useState(0);
  useEffect(() => { asegurarCargada(); return suscribir(() => redibujar(n => n + 1)); }, []);
  const susp = buscarSuspendida({ companyId, name });
  const avisoTitulo = susp ? `⏸ Cliente sin servicio de AyudaTecno — ${susp.name}` : null;

  const anillo = susp ? { boxShadow: '0 0 0 2px #ef4444', position: 'relative' } : null;
  // El punto sólo se dibuja si hay lugar: en 12px taparía el logo entero y se vería como
  // un glitch. Ahí alcanza con el anillo rojo, que igual salta a la vista.
  const punto = susp && s >= 20 ? (
    <span aria-hidden style={{
      position: 'absolute', right: -3, bottom: -3, width: Math.max(9, Math.round(s * 0.34)),
      height: Math.max(9, Math.round(s * 0.34)), borderRadius: '50%', background: '#ef4444',
      color: '#fff', fontSize: Math.max(6, Math.round(s * 0.2)), lineHeight: `${Math.max(9, Math.round(s * 0.34))}px`,
      textAlign: 'center', border: '1.5px solid var(--c-surface, #fff)', fontWeight: 700,
    }}>⏸</span>
  ) : null;

  const base = {
    width: s, height: s, borderRadius: r, flexShrink: 0,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    boxSizing: 'border-box',
    // overflow visible sólo cuando hay punto, para que no lo recorte el borde redondeado.
    overflow: punto ? 'visible' : 'hidden',
    ...anillo, ...style,
  };

  if (logoUrl) {
    return (
      <div
        className={className}
        title={avisoTitulo || title || name || undefined}
        style={{
          ...base,
          padding: pad,
          background: tile ? '#ffffff' : 'transparent',
          border: tile ? '1px solid var(--c-border)' : 'none',
        }}
      >
        <img
          src={logoUrl}
          alt={name || ''}
          loading="lazy"
          style={{ maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto', objectFit: 'contain', display: 'block', borderRadius: 'inherit' }}
        />
        {punto}
      </div>
    );
  }

  // No logo → colored chip with the company's first letter.
  const initial = (name || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className={className}
      title={avisoTitulo || title || name || undefined}
      style={{
        ...base,
        background: labelColor || 'var(--c-brand)',
        color: '#fff',
        fontWeight: 700,
        fontSize: Math.max(8, Math.round(s * 0.46)),
        lineHeight: 1,
      }}
    >
      {initial}
      {punto}
    </div>
  );
}
